import React, { useEffect, useRef, useState } from "react";
import VideoEnforced from "./video/VideoEnforced";
import Quiz from "./quiz/Quiz";
import QuizResultModal from "./quiz/QuizResultModal";
import { useSelector, shallowEqual } from "react-redux";
import * as apiLearning from "../../../api/learning";

export default function LearningModule({ video, quiz, onQuizResult }) {
  const { authToken } = useSelector(({ auth }) => auth, shallowEqual);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const videoId = video?.id || null;
  const [available, setAvailable] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [playbackSessionId, setPlaybackSessionId] = useState(null);
  const [playbackSessionVideoKey, setPlaybackSessionVideoKey] = useState(null);
  const [playbackError, setPlaybackError] = useState(null);
  const progressQueueRef = useRef(Promise.resolve());
  // Stato che abilita i controlli del player se l'utente ha superato il quiz (>=80%)
  const [canControl, setCanControl] = useState(false);
  const quizId = quiz?.id || null;
  const videoKey = `${videoId || ""}:${video?.url || ""}`;
  const activeVideoKeyRef = useRef(videoKey);
  activeVideoKeyRef.current = videoKey;

  useEffect(() => {
    let active = true;
    if (!authToken || !videoId) return;
    apiLearning
      .getGate(authToken, videoId)
      .then((gate) => {
        if (!active) return;
        setAvailable(gate?.available !== false);
        setVideoCompleted(!!gate?.videoCompleted);
      })
      .catch((error) => console.error("Caricamento stato video fallito", error));
    return () => {
      active = false;
    };
  }, [authToken, videoId]);

  useEffect(() => {
    setShowQuiz(false);
    setVideoCompleted(false);
    setCanControl(false);
    setResult(null);
    setSubmissionError(null);
    setPlaybackSessionId(null);
    setPlaybackSessionVideoKey(null);
    setPlaybackError(null);
    progressQueueRef.current = Promise.resolve();
  }, [videoKey]);

  useEffect(() => {
    let active = true;
    if (!authToken || !videoId) return undefined;

    apiLearning
      .startVideoProgress(authToken, videoId)
      .then((session) => {
        if (!active) return;
        setPlaybackSessionId(session?.sessionId || null);
        setPlaybackSessionVideoKey(videoKey);
        setPlaybackError(null);
      })
      .catch((error) => {
        console.error("Avvio sessione di riproduzione fallito", error);
        if (active) setPlaybackError("Impossibile avviare la sessione.");
      });

    return () => {
      active = false;
    };
  }, [authToken, videoId, videoKey]);

  // Recupera eventuali risultati precedenti del quiz per abilitare i controlli
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (!authToken || !videoId || !quizId) return;
        const res = await apiLearning.getQuizResults(authToken, { videoId, quizId });
        // Se l'utente ha già un risultato con percentuale >= 80, abilita i controlli
        const passed = Array.isArray(res) && res.some((r) => parseFloat(r.percent || r.score || 0) >= 80);
        if (active) setCanControl(!!passed);
      } catch (error) {
        console.error("Caricamento risultati quiz fallito", error);
        if (active) setCanControl(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [authToken, videoId, quizId]);

  const handleQuizResult = async (r) => {
    const requestVideoKey = activeVideoKeyRef.current;
    const requestVideoId = videoId;
    const requestQuizId = quizId;
    let finalResult = r;
    setSubmissionError(null);
    if (authToken && requestVideoId && requestQuizId) {
      try {
        const savedResult = await apiLearning.submitQuizResult(authToken, {
          videoId: requestVideoId,
          quizId: requestQuizId,
          answersJson: JSON.stringify(r.answers || {}),
          durationSec: r.durationSec,
        });
        if (activeVideoKeyRef.current !== requestVideoKey) return;
        finalResult = {
          ...r,
          total: savedResult.totalQuestions,
          correct: savedResult.correctCount,
          score: savedResult.percent,
        };
      } catch (error) {
        console.error("Invio risultato quiz fallito", error);
        if (activeVideoKeyRef.current === requestVideoKey) {
          setSubmissionError("Invio risultato fallito. Riprova.");
        }
        return;
      }
    }
    if (activeVideoKeyRef.current !== requestVideoKey) return;
    setResult(finalResult);
    if (parseFloat(finalResult?.score || 0) >= 80) {
      setCanControl(true);
    }
    if (onQuizResult) onQuizResult(finalResult);
  };

  const playbackReady = !authToken || (playbackSessionVideoKey === videoKey && !!playbackSessionId);

  const recordPlaybackProgress = (positionSec) => {
    if (!authToken || !videoId || !playbackReady) return;

    const request = progressQueueRef.current
      .catch(() => undefined)
      .then(() => apiLearning.recordVideoProgress(authToken, playbackSessionId, positionSec));
    progressQueueRef.current = request;

    request
      .then(() => {
        if (activeVideoKeyRef.current === videoKey) setPlaybackError(null);
      })
      .catch((error) => {
        console.error("Sincronizzazione riproduzione fallita", error);
        if (activeVideoKeyRef.current === videoKey) setPlaybackError("Sincronizzazione temporaneamente non riuscita.");
      });
  };

  return (
    <div className="tw:flex tw:flex-col" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {available ? (
        !showQuiz ? (
          <div className="tw:flex-1 tw:min-h-0" style={{ flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <VideoEnforced
              key={videoKey}
              url={video.url}
              progressReady={playbackReady}
              progressError={playbackError}
              onProgress={recordPlaybackProgress}
              // Passa lo stato di abilitazione dei controlli al player
              canControl={canControl}
              onCompleted={async () => {
                if (authToken && videoId) {
                  try {
                    if (!playbackReady || !playbackSessionId) {
                      throw new Error("Sessione di riproduzione non pronta");
                    }
                    await progressQueueRef.current;
                    await apiLearning.markCompleted(authToken, videoId, playbackSessionId);
                    if (activeVideoKeyRef.current === videoKey) setVideoCompleted(true);
                  } catch (error) {
                    console.error("Salvataggio completamento video fallito", error);
                    throw error;
                  }
                } else {
                  setVideoCompleted(true);
                }
              }}
            />
          </div>
        ) : null
      ) : (
        <div className="tw:p-4 tw:bg-yellow-50 tw:text-yellow-800 tw:rounded">
          Contenuto non disponibile per il tuo target.
        </div>
      )}

      {available && (
        <div className="tw:bg-green-50 tw:rounded">
          <div className={`tw:mt-3 tw:text-xl tw:font-bold tw:leading-tight tw:text-center ${videoCompleted ? "tw:text-green-800" : "tw:text-black"}`}>
            {videoCompleted ? "Video completato, ora puoi svolgere il quiz." : "Guarda il video per sbloccare il quiz."}
          </div>
          {videoCompleted && !showQuiz && (
            <div className="tw:mt-3 tw:text-center">
              <button
                className="tw:px-4 tw:py-2 tw:bg-blue-600 tw:text-white tw:rounded"
                onClick={() => setShowQuiz(true)}
              >
                Vai al quiz
              </button>
            </div>
          )}
        </div>
      )}
       {available && videoCompleted && showQuiz && (
         <>
           <Quiz quiz={quiz} result={result} submissionError={submissionError} onResult={handleQuizResult} />
           {submissionError && <div className="tw:p-3 tw:text-red-700 tw:bg-red-50 tw:rounded">{submissionError}</div>}
         </>
       )}

      {result && (
        <QuizResultModal
          result={result}
          onClose={() => {
             setResult(null);
            setShowQuiz(false);
          }}
        />
      )}
    </div>
  );
}
